// versionCheck.js
// Purpose: Handle app version checking and force logout on version mismatch
// Logic: Compares stored version with current version and forces logout if needed

import { clog } from "./utils";

// Update this version number before each production deployment
export const CURRENT_APP_VERSION = "1.0.0";

/**
 * Check if the app version has changed and force logout if needed
 * @returns {boolean} - Returns true if version is valid, false if logout is required
 */
export function checkAppVersion() {
  try {
    const storedVersion = localStorage.getItem("app_version");
    const loginToken = localStorage.getItem("login_token");

    // If no stored version BUT user is logged in, force logout
    // This handles existing logged-in users when feature is first deployed
    if (!storedVersion && loginToken) {
      clog(
        "No stored version found for logged-in user. Forcing logout for version initialization."
      );
      return false; // Force logout to initialize version
    }

    // If no stored version and not logged in, it's a fresh start
    if (!storedVersion) {
      clog(
        "No stored version found. Setting current version:",
        CURRENT_APP_VERSION
      );
      localStorage.setItem("app_version", CURRENT_APP_VERSION);
      return true;
    }

    // Check if versions match
    if (storedVersion !== CURRENT_APP_VERSION) {
      clog(
        `Version mismatch detected. Stored: ${storedVersion}, Current: ${CURRENT_APP_VERSION}`
      );
      return false; // Version mismatch, needs logout
    }

    clog("Version check passed:", CURRENT_APP_VERSION);
    return true; // Version matches
  } catch (error) {
    console.error("Error during version check:", error);
    return true; // Don't block on error
  }
}

/**
 * Update the stored app version to the current version
 * Called after successful login
 */
export function updateAppVersion() {
  try {
    localStorage.setItem("app_version", CURRENT_APP_VERSION);
    clog("App version updated to:", CURRENT_APP_VERSION);
  } catch (error) {
    console.error("Error updating app version:", error);
  }
}

/**
 * Clear the stored app version
 * Called during logout
 */
export function clearAppVersion() {
  try {
    localStorage.removeItem("app_version");
    clog("App version cleared");
  } catch (error) {
    console.error("Error clearing app version:", error);
  }
}

/**
 * Force logout due to version mismatch
 * Clears all user data and redirects to login
 * @param {Function} navigate - React Router navigate function
 */
export function forceLogoutDueToVersion(navigate) {
  try {
    clog("Forcing logout due to version mismatch");

    // Clear all authentication and user data
    localStorage.removeItem("login_token");
    localStorage.removeItem("login_data");
    localStorage.removeItem("workspaces_list");
    localStorage.removeItem("selected_workspace");
    localStorage.removeItem("selectedContentSettings");
    localStorage.removeItem("template_selected");
    localStorage.removeItem("assigning_template_to_file");
    localStorage.removeItem("selected_file");
    localStorage.removeItem("chat_details");
    localStorage.removeItem("chat_file");
    localStorage.removeItem("buyer_persona");
    sessionStorage.clear();

    // Clear app version
    clearAppVersion();

    // Show notification to user
    const message =
      "A new version of the application is available. Please log in again.";

    // Store message for display on login page
    sessionStorage.setItem("version_update_message", message);

    // Redirect to login
    if (navigate) {
      navigate("/", { replace: true });
    } else {
      window.location.href = "/";
    }
  } catch (error) {
    console.error("Error during force logout:", error);
    // Fallback: hard redirect
    window.location.href = "/";
  }
}
